import { RHYTHM_PATH_MISSIONS, type RhythmPathMission } from "./rhythmPathPlan";

const STORAGE_KEY = "bordona-rhythm-path-progress";

const KNOWN_MISSION_IDS = new Set(RHYTHM_PATH_MISSIONS.map((m) => m.id));
const KNOWN_SUB_IDS = new Set(
  RHYTHM_PATH_MISSIONS.flatMap((m) => m.subMissions.map((s) => s.id))
);

export type RhythmPathProgress = {
  completedMissionIds: string[];
  /** IDs de submisión (únicos globalmente). */
  completedSubMissionIds: string[];
};

export function defaultRhythmPathProgress(): RhythmPathProgress {
  return { completedMissionIds: [], completedSubMissionIds: [] };
}

export function loadRhythmPathProgress(): RhythmPathProgress {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultRhythmPathProgress();
    const p = JSON.parse(raw) as Partial<RhythmPathProgress>;
    const missions = Array.isArray(p.completedMissionIds) ? p.completedMissionIds : [];
    const subs = Array.isArray(p.completedSubMissionIds) ? p.completedSubMissionIds : [];
    return {
      completedMissionIds: missions.filter((id) => KNOWN_MISSION_IDS.has(id)),
      completedSubMissionIds: subs.filter((id) => KNOWN_SUB_IDS.has(id)),
    };
  } catch {
    return defaultRhythmPathProgress();
  }
}

export function saveRhythmPathProgress(progress: RhythmPathProgress): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
}

export function markRhythmPathMissionComplete(
  progress: RhythmPathProgress,
  missionId: string
): RhythmPathProgress {
  if (progress.completedMissionIds.includes(missionId)) return progress;
  return {
    ...progress,
    completedMissionIds: [...progress.completedMissionIds, missionId],
  };
}

export function markRhythmSubMissionComplete(
  progress: RhythmPathProgress,
  mission: RhythmPathMission,
  subMissionId: string
): RhythmPathProgress {
  const completedSubMissionIds = progress.completedSubMissionIds.includes(subMissionId)
    ? progress.completedSubMissionIds
    : [...progress.completedSubMissionIds, subMissionId];

  const allSubsDone = mission.subMissions.every((s) =>
    completedSubMissionIds.includes(s.id)
  );

  return {
    completedSubMissionIds,
    completedMissionIds:
      allSubsDone && !progress.completedMissionIds.includes(mission.id)
        ? [...progress.completedMissionIds, mission.id]
        : progress.completedMissionIds,
  };
}

/** Submisión desbloqueada: la anterior hecha, o preview, o índice 0. */
export function isRhythmSubMissionUnlocked(
  mission: RhythmPathMission,
  stepIndex: number,
  completedSubMissionIds: string[],
  preview = false
): boolean {
  if (preview) return true;
  if (stepIndex <= 0) return true;
  const prev = mission.subMissions[stepIndex - 1];
  return prev ? completedSubMissionIds.includes(prev.id) : false;
}
